import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, AlertTriangle } from "lucide-react";
import { fetchProgress, setProgress } from "@/lib/api";

/**
 * Surfaces any constituent kanji the user hasn't yet marked Learning or
 * Known, with one-tap `+` buttons to add them to Learning.
 *
 * Props:
 *   constituents : [{ char, is_kanji, meaning }, ...]
 *   own          : the char this card represents (excluded from suggestions
 *                  so a kanji doesn't suggest itself).
 *   ownStatus    : the user's status on the parent card ('new'|'learning'|'known').
 *                  When 'known' AND prereqs are missing, the panel switches to
 *                  a warning tone — "you've marked the whole thing known, but
 *                  the building blocks aren't yet". Safety net for skim-readers.
 */
export default function PrereqSuggestions({ constituents, own, ownStatus = "new" }) {
  const qc = useQueryClient();
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });
  const m = useMutation({
    mutationFn: (ch) => setProgress(ch, "learning"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["progress"] }),
  });

  const progress = progQ.data?.items || {};
  const missing = (constituents || []).filter((c) => {
    if (!c.is_kanji) return false;
    if (c.char === own) return false;
    const s = progress[c.char]?.status;
    return s !== "known" && s !== "learning";
  });
  if (missing.length === 0) return null;

  const isWarning = ownStatus === "known";
  const tone = isWarning
    ? { border: "border-shu/50", bg: "bg-shu/10", title: "text-shu", icon: AlertTriangle }
    : { border: "border-amber-500/40", bg: "bg-amber-500/5", title: "text-amber-700", icon: null };
  const Icon = tone.icon;

  return (
    <div
      data-testid="prereq-suggestions"
      data-prereq-tone={isWarning ? "warning" : "suggestion"}
      className={`mt-3 px-3 py-2 border ${tone.border} ${tone.bg}`}
    >
      <p className={`font-sans text-[10px] uppercase tracking-[0.25em] mb-2 flex items-center gap-1.5 ${tone.title}`}>
        {Icon && <Icon size={11} />}
        {isWarning
          ? `revisit the building blocks · ${missing.length} not yet mastered`
          : `also start learning · ${missing.length} prerequisite${missing.length === 1 ? "" : "s"}`}
      </p>
      {isWarning && (
        <p className="font-sans text-[10px] text-sumiSoft mb-2 italic">
          You&apos;ve marked this Known, but its constituents aren&apos;t yet. Tap to revisit.
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {missing.map((c) => (
          <button
            key={c.char}
            data-testid={`prereq-add-${c.char}`}
            onClick={() => m.mutate(c.char)}
            title={`Add ${c.char} (${c.meaning || ""}) to your learning deck`}
            className="flex items-center gap-1.5 bg-washi border border-borderDefault hover:border-shu hover:text-shu transition-colors px-2 py-1"
          >
            <span className="font-serif text-base text-sumi">{c.char}</span>
            {c.meaning && (
              <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft truncate max-w-[100px]">
                {c.meaning}
              </span>
            )}
            <Plus size={11} className="text-sumiSoft" />
          </button>
        ))}
      </div>
    </div>
  );
}
